import { useEffect, useState } from "react"
import { useUserContext } from '@/components/providers'

export type DeveloperFeature = 'messageArray' | 'sidebarLinks' | 'showMessageId' | 'promptImprover' | 'markdownTypes' | 'debugPromptArea' | 'adminNotificationBar'
type DeveloperFeatureConfig = {
    enabled: boolean
    title: string
}

export type DeveloperFeatures = Record<DeveloperFeature, DeveloperFeatureConfig>

const DEFAULT_FEATURES: DeveloperFeatures = {
    messageArray: {
        enabled: false,
        title: 'Message Array'
    },
    sidebarLinks: {
        enabled: false,
        title: 'Sidebar Links'
    },
    showMessageId: {
        enabled: false,
        title: 'Show Message ID'
    }, 
    promptImprover: {
        enabled: false,
        title: 'Prompt Improver'
    },
    markdownTypes: {
        enabled: false,
        title: 'Markdown Types'
    }, 
    debugPromptArea: {
        enabled: false,
        title: 'Debug Prompt Area'
    },
    adminNotificationBar: {
        enabled: true,
        title: 'Admin Notification Bar'
    }
}

export function useDeveloperFeatures() {
    const { isAdmin } = useUserContext()
    const [features, setFeatures] = useState<DeveloperFeatures>(DEFAULT_FEATURES)
    
    useEffect(() => {
        if (!isAdmin) return

        // Ensure the developerFeatures key exists for early consumers (e.g., layout scripts)
        const existing = localStorage.getItem('developerFeatures')
        if (!existing) {
            localStorage.setItem('developerFeatures', '{}')
        }

        // Initialize state from localStorage
        const savedFeatures = localStorage.getItem('developerFeatures')
        if (savedFeatures) {
            try {
                const parsedFeatures = JSON.parse(savedFeatures) as DeveloperFeatures
                setFeatures({ ...DEFAULT_FEATURES, ...parsedFeatures })
            } catch (error) {
                console.error('Failed to parse developer features from localStorage:', error)
                setFeatures(DEFAULT_FEATURES)
            }
        }
        
        // Listen for changes from other components
        const handleDevFeaturesChanged = () => {
            const savedFeatures = localStorage.getItem('developerFeatures')
            if (savedFeatures) {
                try {
                    const parsedFeatures = JSON.parse(savedFeatures) as DeveloperFeatures
                    setFeatures({ ...DEFAULT_FEATURES, ...parsedFeatures })
                } catch (error) {
                    setFeatures(DEFAULT_FEATURES)
                }
            }
        }
        
        window.addEventListener('devFeaturesChanged', handleDevFeaturesChanged)
        
        return () => {
            window.removeEventListener('devFeaturesChanged', handleDevFeaturesChanged)
        }
    }, [isAdmin])
    
    const toggleFeature = (feature: DeveloperFeature, enabled: boolean) => {
        if (!isAdmin) return
        const updatedFeatures = { ...features, [feature]: { enabled, title: features[feature].title } }
        
        // Store in localStorage
        localStorage.setItem('developerFeatures', JSON.stringify(updatedFeatures))
        // Update local state
        setFeatures(updatedFeatures)
        // Dispatch event to notify other components
        window.dispatchEvent(new Event('devFeaturesChanged'))
    }
    
    const isFeatureEnabled = (feature: DeveloperFeature): boolean => {
        return features[feature].enabled || false
    }
    
    const toggleAllFeatures = (enabled: boolean) => {
        if (!isAdmin) return
        const updatedFeatures = Object.keys(features).reduce((acc, key) => {
            acc[key as DeveloperFeature] = {
                enabled,
                title: features[key as DeveloperFeature].title
            }
            return acc
        }, {} as DeveloperFeatures)
        
        localStorage.setItem('developerFeatures', JSON.stringify(updatedFeatures))
        setFeatures(updatedFeatures)
        window.dispatchEvent(new Event('devFeaturesChanged'))
    }
    
    return {
        features,
        toggleFeature,
        isFeatureEnabled,
        toggleAllFeatures
    }
}